import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Segment,
  Input,
  Container,
  Form,
  Button,
  Header,
  Icon,
  Dropdown,
  Grid,
} from "semantic-ui-react";

import { Link } from "react-router-dom";

import {
  auth,
  db,
} from "../firebase/firebaseConfig";

import {
  getDocs,
  collection,
  where,
  query,
} from "firebase/firestore";

import Alerta from "../components/Alertas";
import AgregarProveedorDb from "../firebase/AgregarProveedorDb";

import { FaRegEdit } from "react-icons/fa";

import validarRut from "../funciones/validarRut";
import { UserContext } from "../context/UserContext";
import ExportarExcel from "../funciones/ExportarExcel";

// ==========================================================
// CATEGORÍAS DE PROVEEDOR
// ==========================================================

const categoriasProveedor = [
  {
    key: "st_externo",
    value: "ST_EXTERNO",
    text: "Servicio Técnico Externo",
    icon: "wrench",
    description: "Servicio técnico externo",
  },
  {
    key: "proveedor_equipos",
    value: "PROVEEDOR_EQUIPOS",
    text: "Proveedor de Equipos",
    icon: "box",
    description: "Venta de equipos médicos",
  },
  {
    key: "arriendo",
    value: "ARRIENDO",
    text: "Arriendo",
    icon: "clipboard outline",
    description: "Arriendo de equipos",
  },
  {
    key: "transporte",
    value: "TRANSPORTE",
    text: "Transporte",
    icon: "truck",
    description: "Transportistas y courier",
  },
  {
    key: "insumos",
    value: "INSUMOS",
    text: "Insumos",
    icon: "archive",
    description: "Insumos y consumibles",
  },
  {
    key: "otros",
    value: "OTROS",
    text: "Otros",
    icon: "ellipsis horizontal",
    description: "Otros proveedores",
  },
];

// ==========================================================
// COMPONENTE
// ==========================================================

const Proveedores = () => {
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  // ========================================================
  // FORMULARIO
  // ========================================================

  const [rut, setRut] = useState("");
  const [entidad, setEntidad] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [correo, setCorreo] = useState("");
  const [nomContacto, setNomContacto] = useState("");
  const [categoria, setCategoria] = useState("");

  // ========================================================
  // ALERTAS
  // ========================================================

  const [alerta, cambiarAlerta] = useState({});
  const [estadoAlerta, cambiarEstadoAlerta] = useState(false);

  // ========================================================
  // LISTADO
  // ========================================================

  const [buscador, setBuscador] = useState("");
  const [leer, setLeer] = useState([]);
  const [flag, setFlag] = useState(false);

  // ========================================================
  // ESTADO GUARDADO
  // ========================================================

  const [guardando, setGuardando] = useState(false);

  // ========================================================
  // OBTENER PROVEEDORES
  // ========================================================

  const getData = async () => {
    if (!users?.emp_id) return;

    try {
      const traerProveedor = collection(db, "proveedores");

      const dato = query(
        traerProveedor,
        where("emp_id", "==", users.emp_id)
      );

      const data = await getDocs(dato);

      const proveedores = data.docs.map((documento, index) => ({
        ...documento.data(),
        id: documento.id,
        id2: index + 1,
      }));

      proveedores.sort((a, b) =>
        String(a.nombre || "").localeCompare(
          String(b.nombre || "")
        )
      );

      setLeer(proveedores);
    } catch (error) {
      console.error("Error obteniendo proveedores:", error);

      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "No fue posible cargar los proveedores",
      });
    }
  };

  // ========================================================
  // CARGAR PROVEEDORES
  // ========================================================

  useEffect(() => {
    getData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag, users?.emp_id]);

  // ========================================================
  // BUSCADOR
  // ========================================================

  const filtroProveedor = leer.filter((item) =>
    Object.keys(item).some((key) =>
      String(item[key] ?? "")
        .toLowerCase()
        .includes(buscador.toLowerCase())
    )
  );

  // ========================================================
  // DETECTAR RUT REPETIDO
  // ========================================================

  const detectar = async (e) => {
    cambiarEstadoAlerta(false);
    cambiarAlerta({});

    const RUT_LOGISTICMED_ST = "76550891-6";

    if (
      (e.key === "Enter" || e.key === "Tab") &&
      rut &&
      users?.emp_id
    ) {
      try {
        const rutNormalizado = rut.trim();

        const p = query(
          collection(db, "proveedores"),
          where("emp_id", "==", users.emp_id),
          where("rut", "==", rutNormalizado)
        );

        const rutProv = await getDocs(p);

        if (rutProv.docs.length !== 0) {
          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje: "Ya existe RUT de proveedor",
          });

          return;
        }

        if (rutNormalizado === RUT_LOGISTICMED_ST) {
          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Este RUT corresponde al Servicio Técnico LogisticMed y no puede registrarse como proveedor externo.",
          });

          return;
        }
      } catch (error) {
        console.error("Error validando RUT proveedor:", error);
      }
    }
  };

  // ========================================================
  // LIMPIAR FORMULARIO
  // ========================================================

  const limpiarFormulario = () => {
    setRut("");
    setEntidad("");
    setDireccion("");
    setTelefono("");
    setCorreo("");
    setNomContacto("");
    setCategoria("");
  };

  // ========================================================
  // GUARDAR PROVEEDOR
  // ========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    cambiarEstadoAlerta(false);
    cambiarAlerta({});

    if (guardando) return;

    const rutNormalizado = rut.trim();

    const existe =
      leer.filter(
        (cli) =>
          String(cli.rut || "").trim() === rutNormalizado
      ).length === 0;

    const expresionRegular =
      /[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/;

    const expresionRegularRut =
      /^[0-9]+[-|‐]{1}[0-9kK]{1}$/;

    const RUT_LOGISTICMED_ST = "76550891-6";

    const temp = rutNormalizado.split("-");

    let digito = temp[1];

    if (digito === "k" || digito === "K") {
      digito = -1;
    }

    const validaR = validarRut(rutNormalizado);

    // ======================================================
    // VALIDACIONES
    // ======================================================

    if (rutNormalizado === "") {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Campo RUT no puede estar vacío",
      });

      return;
    }

    if (!expresionRegularRut.test(rutNormalizado)) {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Formato incorrecto de RUT",
      });

      return;
    }

    if (validaR !== parseInt(digito)) {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "RUT no válido",
      });

      return;
    }

    if (entidad.trim() === "") {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Campo Razón Social no puede estar vacío",
      });

      return;
    }

    if (direccion.trim() === "") {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Campo Dirección no puede estar vacío",
      });

      return;
    }

    if (telefono.trim() === "") {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Campo Teléfono no puede estar vacío",
      });

      return;
    }

    if (!expresionRegular.test(correo.trim())) {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Favor ingresar un correo válido",
      });

      return;
    }

    if (nomContacto.trim() === "") {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Campo Contacto no puede estar vacío",
      });

      return;
    }

    if (categoria === "") {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Debe seleccionar una categoría",
      });

      return;
    }

    if (rutNormalizado === RUT_LOGISTICMED_ST) {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje:
          "Este RUT corresponde al Servicio Técnico LogisticMed y no puede registrarse como proveedor externo.",
      });

      return;
    }

    if (!existe) {
      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "RUT ya existe",
      });

      return;
    }

    // ======================================================
    // GUARDAR
    // ======================================================

    try {
      setGuardando(true);

      await AgregarProveedorDb({
        rut: rutNormalizado,

        nombre: entidad
          .toLocaleUpperCase()
          .trim(),

        direccion: direccion
          .toLocaleUpperCase()
          .trim(),

        telefono: telefono.trim(),

        correo: correo
          .toLocaleLowerCase()
          .trim(),

        contacto: nomContacto
          .toLocaleUpperCase()
          .trim(),

        categoria,

        activo: true,

        userAdd: user?.email || "",
        userMod: user?.email || "",

        fechaAdd: new Date(),
        fechaMod: new Date(),

        emp_id: users.emp_id,
      });

      limpiarFormulario();

      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "exito",
        mensaje: "Proveedor registrado exitosamente",
      });

      setFlag((prev) => !prev);
    } catch (error) {
      console.error(
        "Se produjo un error al guardar proveedor:",
        error
      );

      cambiarEstadoAlerta(true);

      cambiarAlerta({
        tipo: "error",
        mensaje: "Error al guardar proveedor",
      });
    } finally {
      setGuardando(false);
    }
  };

  // ========================================================
  // EXPORTAR EXCEL
  // ========================================================

  const ExportarXls = () => {
    if (leer.length === 0) return;

    const columnsToShow = [
      "rut",
      "nombre",
      "direccion",
      "telefono",
      "correo",
      "contacto",
      "categoria",
    ];

    const excelBlob = ExportarExcel(
      leer,
      columnsToShow
    );

    const excelURL =
      URL.createObjectURL(excelBlob);

    const downloadLink =
      document.createElement("a");

    downloadLink.href = excelURL;
    downloadLink.download = "proveedores.xlsx";

    document.body.appendChild(downloadLink);

    downloadLink.click();

    document.body.removeChild(downloadLink);

    URL.revokeObjectURL(excelURL);
  };

  // ========================================================
  // RENDER
  // ========================================================

  return (
    <Container
      fluid
      style={{
        maxWidth: 1400,
        padding: "24px 28px",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* ==================================================== */}
      {/* ENCABEZADO */}
      {/* ==================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              minWidth: 48,
              borderRadius: 12,
              background: "#e5f7f7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon
              name="building outline"
              style={{
                margin: 0,
                color: "#0d9da5",
                fontSize: 21,
              }}
            />
          </div>

          <div>
            <Header
              as="h2"
              style={{
                margin: 0,
                color: "#172b4d",
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              Proveedores
            </Header>

            <div
              style={{
                color: "#8793a5",
                fontSize: 13,
                marginTop: 4,
              }}
            >
              Gestione y mantenga el registro de proveedores de su empresa
            </div>
          </div>
        </div>

        <Button
          type="button"
          onClick={ExportarXls}
          disabled={leer.length === 0}
          style={{
            background: "#ffffff",
            color: "#526273",
            border: "1px solid #d7dee7",
            borderRadius: 6,
            boxShadow: "none",
          }}
        >
          <Icon
            name="file excel outline"
            style={{
              color: "#21ba45",
            }}
          />

          Exportar Excel
        </Button>
      </div>

      {/* ==================================================== */}
      {/* FORMULARIO */}
      {/* ==================================================== */}

      <Segment
        style={{
          borderRadius: 8,
          border: "1px solid #e5e9ef",
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
          padding: 0,
          background: "#ffffff",
          overflow: "visible",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #edf0f4",
            display: "flex",
            alignItems: "center",
            gap: 9,
            color: "#253858",
            fontWeight: 600,
          }}
        >
          <Icon
            name="plus circle"
            style={{
              color: "#0d9da5",
            }}
          />

          Registrar proveedor
        </div>

        <div
          style={{
            padding: 18,
          }}
        >
          <Form
            size="small"
            onSubmit={handleSubmit}
          >
            {/* ============================================== */}
            {/* DATOS GENERALES */}
            {/* ============================================== */}

            <div style={sectionTitleStyle}>
              <Icon
                name="building outline"
                style={{
                  color: "#0d9da5",
                }}
              />

              Datos generales
            </div>

            <Grid
              stackable
              columns={5}
            >
              {/* RUT */}

              <Grid.Column>
                <Form.Input
                  label="RUT"
                  fluid
                  icon={
                    <Icon
                      name="address card"
                      style={{
                        color: "#2185d0",
                      }}
                    />
                  }
                  iconPosition="left"
                  placeholder="Ingrese RUT"
                  value={rut}
                  onChange={(e) =>
                    setRut(e.target.value)
                  }
                  onKeyDown={detectar}
                />
              </Grid.Column>

              {/* RAZÓN SOCIAL */}

              <Grid.Column>
                <Form.Input
                  label="Razón Social"
                  fluid
                  icon={
                    <Icon
                      name="building outline"
                      style={{
                        color: "#0d9da5",
                      }}
                    />
                  }
                  iconPosition="left"
                  placeholder="Ingrese Razón Social"
                  value={entidad}
                  onChange={(e) =>
                    setEntidad(e.target.value)
                  }
                />
              </Grid.Column>

              {/* DIRECCIÓN */}

              <Grid.Column>
                <Form.Input
                  label="Dirección"
                  fluid
                  icon={
                    <Icon
                      name="map marker alternate"
                      style={{
                        color: "#f2711c",
                      }}
                    />
                  }
                  iconPosition="left"
                  placeholder="Ingrese Dirección"
                  value={direccion}
                  onChange={(e) =>
                    setDireccion(e.target.value)
                  }
                />
              </Grid.Column>

              {/* TELÉFONO */}

              <Grid.Column>
                <Form.Input
                  label="Teléfono"
                  fluid
                  icon={
                    <Icon
                      name="phone"
                      style={{
                        color: "#21ba45",
                      }}
                    />
                  }
                  iconPosition="left"
                  placeholder="Ingrese Teléfono"
                  value={telefono}
                  onChange={(e) =>
                    setTelefono(e.target.value)
                  }
                />
              </Grid.Column>

              {/* EMAIL */}

              <Grid.Column>
                <Form.Input
                  label="Email"
                  fluid
                  icon={
                    <Icon
                      name="mail"
                      style={{
                        color: "#6435c9",
                      }}
                    />
                  }
                  iconPosition="left"
                  placeholder="Ingrese Email"
                  value={correo}
                  onChange={(e) =>
                    setCorreo(e.target.value)
                  }
                />
              </Grid.Column>
            </Grid>

            {/* ============================================== */}
            {/* CLASIFICACIÓN */}
            {/* ============================================== */}

            <div
              style={{
                ...sectionTitleStyle,
                marginTop: 18,
              }}
            >
              <Icon
                name="tags"
                style={{
                  color: "#f2711c",
                }}
              />

              Clasificación y contacto
            </div>

            <Grid
              stackable
              columns={3}
            >
              {/* CATEGORÍA */}

              <Grid.Column width={5}>
                <Form.Field>
                  <label>
                    Categoría
                  </label>

                  <Dropdown
                    fluid
                    selection
                    search
                    clearable
                    placeholder="Seleccione una categoría"
                    options={categoriasProveedor}
                    value={categoria}
                    onChange={(e, { value }) =>
                      setCategoria(value)
                    }
                  />
                </Form.Field>
              </Grid.Column>

              {/* CONTACTO */}

              <Grid.Column width={7}>
                <Form.Input
                  label="Contacto"
                  fluid
                  icon={
                    <Icon
                      name="user"
                      style={{
                        color: "#2185d0",
                      }}
                    />
                  }
                  iconPosition="left"
                  placeholder="Ingrese Nombre de Contacto"
                  value={nomContacto}
                  onChange={(e) =>
                    setNomContacto(e.target.value)
                  }
                />
              </Grid.Column>

              {/* GUARDAR */}

              <Grid.Column width={4}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  &nbsp;
                </label>

                <Button
                  fluid
                  type="submit"
                  loading={guardando}
                  disabled={guardando}
                  style={{
                    height: 38,
                    borderRadius: 6,
                    fontWeight: 600,
                    background: "#0d9da5",
                    color: "#ffffff",
                    boxShadow: "none",
                  }}
                >
                  {!guardando && (
                    <Icon name="save" />
                  )}

                  Guardar
                </Button>
              </Grid.Column>
            </Grid>
          </Form>
        </div>
      </Segment>

      {/* ==================================================== */}
      {/* LISTADO */}
      {/* ==================================================== */}

      <Segment
        style={{
          borderRadius: 8,
          border: "1px solid #e5e9ef",
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
          padding: 0,
          background: "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* ================================================== */}
        {/* CABECERA LISTADO */}
        {/* ================================================== */}

        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #edf0f4",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 15,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#253858",
              fontWeight: 600,
            }}
          >
            <Icon
              name="list"
              style={{
                color: "#0d9da5",
              }}
            />

            Proveedores registrados

            <span
              style={{
                marginLeft: 8,
                color: "#98a1af",
                fontSize: 11,
                fontWeight: 400,
              }}
            >
              {filtroProveedor.length} registros
            </span>
          </div>

          <div
            style={{
              width: 350,
              maxWidth: "100%",
            }}
          >
            <Input
              fluid
              icon={
                <Icon
                  name="search"
                  style={{
                    color: "#2185d0",
                  }}
                />
              }
              iconPosition="left"
              placeholder="Buscar proveedores..."
              value={buscador}
              onChange={(e) =>
                setBuscador(e.target.value)
              }
            />
          </div>
        </div>

        {/* ================================================== */}
        {/* TABLA */}
        {/* ================================================== */}

        <div
          style={{
            maxHeight: 520,
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <Table
            celled
            selectable
            compact
            unstackable
            style={{
              margin: 0,
              border: 0,
              borderRadius: 0,
              minWidth: 1100,
              fontSize: 12,
            }}
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell
                  width={1}
                  textAlign="center"
                  style={stickyTh}
                >
                  N°
                </Table.HeaderCell>

                <Table.HeaderCell
                  width={4}
                  style={stickyTh}
                >
                  Razón Social
                </Table.HeaderCell>

                <Table.HeaderCell
                  width={2}
                  style={stickyTh}
                >
                  RUT
                </Table.HeaderCell>

                <Table.HeaderCell
                  width={4}
                  style={stickyTh}
                >
                  Dirección
                </Table.HeaderCell>

                <Table.HeaderCell
                  width={2}
                  style={stickyTh}
                >
                  Teléfono
                </Table.HeaderCell>

                <Table.HeaderCell
                  width={3}
                  style={stickyTh}
                >
                  Categoría
                </Table.HeaderCell>

                <Table.HeaderCell
                  width={2}
                  textAlign="center"
                  style={stickyTh}
                >
                  Acción
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {filtroProveedor.length === 0 ? (
                <Table.Row>
                  <Table.Cell
                    colSpan={7}
                    textAlign="center"
                    style={{
                      padding: 32,
                      color: "#8995a3",
                    }}
                  >
                    <Icon
                      name="info circle"
                      style={{
                        color: "#2185d0",
                      }}
                    />

                    No existen proveedores para mostrar
                  </Table.Cell>
                </Table.Row>
              ) : (
                filtroProveedor.map(
                  (item, index) => (
                    <Table.Row key={item.id}>
                      <Table.Cell textAlign="center">
                        {index + 1}
                      </Table.Cell>

                      <Table.Cell>
                        <div
                          style={{
                            fontWeight: 600,
                            color: "#344563",
                          }}
                        >
                          {item.nombre || "-"}
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        {item.rut || "-"}
                      </Table.Cell>

                      <Table.Cell>
                        {item.direccion || "-"}
                      </Table.Cell>

                      <Table.Cell>
                        {item.telefono || "-"}
                      </Table.Cell>

                      <Table.Cell>
                        {item.categoria || "-"}
                      </Table.Cell>

                      <Table.Cell textAlign="center">
                        <Link
                          to={`/actualizaproveedor/${item.id}`}
                        >
                          <Button
                            basic
                            icon
                            size="small"
                            title="Editar proveedor"
                            style={{
                              borderRadius: 6,
                              color: "#2185d0",
                              borderColor: "#b9d6ee",
                              background: "#f7fbff",
                            }}
                          >
                            <FaRegEdit
                              style={{
                                fontSize: 16,
                                color: "#2185d0",
                              }}
                            />
                          </Button>
                        </Link>
                      </Table.Cell>
                    </Table.Row>
                  )
                )
              )}
            </Table.Body>
          </Table>
        </div>

        {/* ================================================== */}
        {/* PIE */}
        {/* ================================================== */}

        {leer.length > 0 && (
          <div
            style={{
              padding: "12px 18px",
              borderTop: "1px solid #edf0f4",
              color: "#697586",
              fontSize: 12,
            }}
          >
            Mostrando{" "}
            <strong>
              {filtroProveedor.length}
            </strong>{" "}
            de{" "}
            <strong>
              {leer.length}
            </strong>{" "}
            proveedores
          </div>
        )}
      </Segment>

      {/* ==================================================== */}
      {/* ALERTAS */}
      {/* ==================================================== */}

      <Alerta
        tipo={alerta.tipo}
        mensaje={alerta.mensaje}
        estadoAlerta={estadoAlerta}
        cambiarEstadoAlerta={cambiarEstadoAlerta}
      />
    </Container>
  );
};

// ==========================================================
// ESTILOS COMUNES
// ==========================================================

const sectionTitleStyle = {
  color: "#344563",
  fontSize: 12,
  fontWeight: 700,
  marginBottom: 10,
  marginTop: 2,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  display: "flex",
  alignItems: "center",
  gap: 4,
};

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#344563",
  fontWeight: 600,
};

export default Proveedores;